import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuthContext } from '../context/AuthContext';

const DEFAULT_REDIRECT_DELAY_MS = 5000;

const getTokenFromSearch = (search) => {
  if (!search) return null;
  try {
    const params = new URLSearchParams(search);
    return params.get('token');
  } catch {
    return null;
  }
};

const useVerifyEmailFlow = ({ redirectDelayMs = DEFAULT_REDIRECT_DELAY_MS } = {}) => {
  const { verifyEmail } = useAuthContext();
  const [status, setStatus] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [countdown, setCountdown] = useState(null);
  const hasRequestedRef = useRef(false);

  const redirectToLogin = useCallback(() => {
    if (typeof window === 'undefined') return;
    window.location.replace('/');
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const token = getTokenFromSearch(window.location.search || '');

    if (!token) {
      setStatus('missing');
      return undefined;
    }

    if (hasRequestedRef.current) {
      return undefined;
    }
    hasRequestedRef.current = true;

    let isMounted = true;
    setStatus('verifying');

    verifyEmail(token)
      .then(() => {
        if (!isMounted) return;
        setStatus('success');
      })
      .catch((error) => {
        if (!isMounted) return;
        setErrorMessage(error?.message || '');
        setStatus('error');
      });

    return () => {
      isMounted = false;
    };
  }, [verifyEmail]);

  useEffect(() => {
    if (!['success', 'error', 'missing'].includes(status)) {
      return undefined;
    }

    const totalSeconds = Math.max(1, Math.ceil(redirectDelayMs / 1000));
    setCountdown(totalSeconds);

    const intervalId = window.setInterval(() => {
      setCountdown((prev) => {
        if (prev === null) return prev;
        if (prev <= 1) return 0;
        return prev - 1;
      });
    }, 1000);

    const timeoutId = window.setTimeout(() => {
      redirectToLogin();
    }, redirectDelayMs);

    return () => {
      window.clearInterval(intervalId);
      window.clearTimeout(timeoutId);
    };
  }, [redirectDelayMs, redirectToLogin, status]);

  return {
    status,
    errorMessage,
    countdown,
    redirectToLogin
  };
};

export default useVerifyEmailFlow;
